import Title from '@cohort/components-xps/components/Title';
import {getImageUrl, getVideoUrl, Sizes} from '@cohort/shared/utils/media';
import {classNames} from '@cohort/shared-frontend/utils/classNames';
import PrimaryButton from '@cohort/wallet/components/button/PrimaryButton';
import BaseModal from '@cohort/wallet/components/modals/BaseModal';
import {Subtitle} from '@cohort/wallet/components/Subtitle';
import useThemeContext from '@cohort/wallet/hooks/useThemeContext';
import type {CampaignJourneyWDto} from '@cohort/wallet-schemas/campaign';
import type {JourneyParticipationWDto} from '@cohort/wallet-schemas/journeyParticipation';
import {Fire} from '@phosphor-icons/react';
import {useTranslation} from 'react-i18next';

type JourneyClaimSuccessProps = {
  campaign: CampaignJourneyWDto;
  participation: JourneyParticipationWDto;
  onClose: () => void;
};

type DigitalAssetPreviewProps = {
  campaign: CampaignJourneyWDto;
};

const DigitalAssetPreview: React.FC<DigitalAssetPreviewProps> = ({campaign}) => {
  const {hasDarkBg} = useThemeContext();

  return (
    <div className="flex w-[var(--thin-card-width)] flex-col items-center">
      <div className="relative overflow-hidden rounded-t-xl">
        {!campaign.collection.animationFileKey ? (
          <img
            src={getImageUrl(import.meta.env.COHORT_ENV, campaign.collection.imageFileKey, {
              h: Sizes.S,
              w: Sizes.S,
            })}
            className="rounded-t-xl object-cover object-center"
          />
        ) : (
          <video
            controls={false}
            disablePictureInPicture
            disableRemotePlayback
            autoPlay
            playsInline
            webkit-playsinline="true"
            loop
            muted
            className="rounded-t-xl object-cover object-center"
          >
            <source
              src={getVideoUrl(import.meta.env.COHORT_ENV, campaign.collection.animationFileKey, {
                h: Sizes.S,
                w: Sizes.S,
              })}
            />
            <img
              src={getImageUrl(import.meta.env.COHORT_ENV, campaign.collection.imageFileKey, {
                h: Sizes.S,
                w: Sizes.S,
              })}
              className="rounded-t-xl object-cover object-center"
            />
          </video>
        )}
      </div>
      <div
        className={classNames(
          'w-full rounded-b-xl p-4 font-bold',
          hasDarkBg ? 'bg-white/8 text-white' : 'bg-black/4 text-black/90'
        )}
      >
        {campaign.collection.name}
      </div>
    </div>
  );
};

const JourneyCompletionSuccess: React.FC<JourneyClaimSuccessProps> = ({
  campaign,
  participation,
  onClose,
}) => {
  const {t} = useTranslation('components', {keyPrefix: 'modals.journeys.journeyClaimSuccess'});

  return (
    <BaseModal
      title={
        <div className="flex w-full flex-col items-center justify-center gap-2">
          <Fire className="h-8 w-8" />
          <Title content={t('title')} classes="text-xl font-semibold !w-auto" />
        </div>
      }
      subtitle={
        <div className="text-center">
          <Subtitle
            content={
              participation.rewardClaimed ? t('subtitleRewardClaimed') : t('subtitleNoReward')
            }
            classes="!text-base font-medium"
          />
        </div>
      }
      tracking={{namespace: 'modals.journeys.journeyClaimSuccess'}}
    >
      {participation.rewardClaimed && (
        <div className="mb-4 flex justify-center">
          <DigitalAssetPreview campaign={campaign} />
        </div>
      )}
      <div className="text-center">
        <PrimaryButton
          text={t('cta')}
          type="button"
          tracking={{
            namespace: 'modals.journeys.journeyClaimSuccess.close',
            metadata: {
              journeyId: campaign.id,
            },
          }}
          onClick={onClose}
        />
      </div>
    </BaseModal>
  );
};

export default JourneyCompletionSuccess;
